import React, { useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { LeagueLogo, LEAGUE_LOGOS } from "@/components/LeagueLogo";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back to GameEdge");
      nav(loc.state?.from ?? "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      <div className="hidden lg:flex flex-1 flex-col justify-between bg-primary p-12 text-primary-foreground grid-pattern relative overflow-hidden">
        <div className="relative">
          <Link to="/" className="inline-flex" data-testid="login-logo-link">
            <img src="/logo.svg" alt="GameEdge" className="h-7 w-auto" draggable={false} />
          </Link>
        </div>
        <div className="relative space-y-6">
          <h2 className="font-lexend text-4xl font-bold leading-tight text-white 2xl:text-5xl">
            Play the game <span className="text-secondary-lighter">before</span> it's played.
          </h2>
          <p className="max-w-md text-sm leading-relaxed text-gray-300">
            Professional-grade Monte Carlo simulations, closing line value tracking, and an AI-powered
            edge analyst — all in one dashboard.
          </p>
          <div className="pt-4">
            <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              Covering all major leagues
            </div>
            <div className="flex flex-wrap items-center gap-2.5" data-testid="login-league-logos">
              {Object.keys(LEAGUE_LOGOS).map((code) => (
                <div
                  key={code}
                  className="group flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 backdrop-blur transition-colors hover:bg-white/10"
                  data-testid={`login-league-${code}`}
                  title={code}
                >
                  <LeagueLogo code={code} size={24} />
                  <span className="text-xs font-semibold tracking-wide text-gray-200">{code}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="relative text-xs text-gray-500">
          © {new Date().getFullYear()} GameEdge. All rights reserved.
        </div>
      </div>

      <div className="flex w-full flex-1 items-center justify-center bg-gray-0 p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div>
            <div className="iso-label">Sign in</div>
            <h1 className="mt-1 font-lexend">Welcome back, edge-seeker.</h1>
            <p className="mt-1.5 text-sm text-gray-500">
              Don't have an account?{" "}
              <Link to="/register" className="font-semibold text-secondary hover:underline" data-testid="go-register-link">
                Create one
              </Link>
            </p>
          </div>
          <div>
            <label className="iso-label">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="iso-input"
              data-testid="login-email"
              required
            />
          </div>
          <div>
            <label className="iso-label">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="iso-input"
              data-testid="login-password"
              required
            />
          </div>
          <button type="submit" disabled={busy} className="iso-btn-primary w-full" data-testid="login-submit">
            {busy && <Loader2 className="size-4 animate-spin" />}
            Sign in
          </button>
        </form>
      </div>
    </div>
  );
}
